import { useCallback, useEffect, useState } from "react";
import Controls from "./components/Controls";
import Cube3D from "./components/Cube3D";
import {
  applyMove,
  generateScramble,
  initialState,
  isSolved,
  moveToString,
  reverseScramble,
  type CubeState,
  type Move,
} from "./lib/cube";

const ANIM_MS = 220;

export default function App() {
  const [state, setState] = useState<CubeState>(initialState);
  const [queue, setQueue] = useState<Move[]>([]);
  const [animation, setAnimation] = useState<{ move: Move; progress: number } | null>(
    null,
  );

  // Drain the queue when nothing is animating.
  useEffect(() => {
    if (animation) return;
    if (queue.length === 0) return;
    const [next, ...rest] = queue;
    setAnimation({ move: next, progress: 0 });
    setQueue(rest);
  }, [animation, queue]);

  // Drive the active animation per-frame.
  useEffect(() => {
    if (!animation) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ANIM_MS);
      if (t < 1) {
        setAnimation((a) => (a ? { ...a, progress: t } : null));
        raf = requestAnimationFrame(tick);
      } else {
        setState((s) => applyMove(s, animation.move));
        setAnimation(null);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animation?.move.face, animation?.move.prime, animation?.move.double]);

  const busy = animation !== null || queue.length > 0;
  const solved = isSolved(state) && !busy;

  // User-initiated single move — invalidates the saved scramble.
  const userMove = useCallback(
    (m: Move) => {
      if (busy) return;
      setState((s) => ({ ...s, scrambleMoves: [] }));
      setQueue((q) => [...q, m]);
    },
    [busy],
  );

  const scramble = useCallback(() => {
    if (busy) return;
    const moves = generateScramble(25);
    setState({ ...initialState(), scrambleMoves: moves });
    setQueue(moves);
  }, [busy]);

  const solve = useCallback(() => {
    if (busy || state.scrambleMoves.length === 0) return;
    setQueue(reverseScramble(state));
  }, [busy, state]);

  const reset = useCallback(() => {
    setState(initialState());
    setQueue([]);
    setAnimation(null);
  }, []);

  // Keyboard shortcuts: U D F B L R (Shift = prime, 2 = double via key '2' modifier)
  useEffect(() => {
    let pendingDouble = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toUpperCase();
      if (key === "2") {
        pendingDouble = true;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => (pendingDouble = false), 800);
        return;
      }
      if ("UDFBLR".includes(key)) {
        e.preventDefault();
        const modifier = pendingDouble
          ? "double"
          : e.shiftKey
            ? "prime"
            : undefined;
        userMove({
          face: key as Move["face"],
          prime: modifier === "prime",
          double: modifier === "double",
        });
        pendingDouble = false;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        return;
      }
      if (e.key === "Escape") {
        reset();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (timer) clearTimeout(timer);
    };
  }, [userMove, reset]);

  return (
    <div className="app">
      <div className="canvas-wrap">
        <Cube3D state={state} animation={animation} />

        <div className="hud-top-left">
          <span className="hud-pill">
            <span className="hud-label">Moves</span>
            <span className="hud-num">{state.history.length}</span>
          </span>
          {state.scrambleMoves.length > 0 && !busy && !solved && (
            <span className="hud-pill solvable">scramble saved</span>
          )}
          {solved && <span className="hud-pill solved">solved</span>}
          {busy && <span className="hud-pill busy">animating…</span>}
        </div>

        <div className="hud-top-right">
          <span className="hud-meta">drag to rotate · pinch to zoom</span>
        </div>
      </div>

      <Controls
        onMove={userMove}
        onScramble={scramble}
        onSolve={solve}
        onReset={reset}
        canSolve={state.scrambleMoves.length > 0 && !busy}
        canScramble={!busy}
        busy={busy}
        history={state.history.map(moveToString)}
        scramble={state.scrambleMoves.map(moveToString)}
      />
    </div>
  );
}
